import React, { useState, useEffect } from 'react';
import { Shield, CheckCircle2, AlertCircle, BookOpen, Pencil, Volume2, MessageCircle, Bug, Loader2, Mic, BookMarked, Lock, Mail } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Generate or retrieve device UUID for device enrollment
 */
const getOrCreateDeviceId = () => {
  let deviceId = localStorage.getItem('deviceId');
  if (!deviceId) {
    // Generate UUID v4
    deviceId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
    localStorage.setItem('deviceId', deviceId);
  }
  return deviceId;
};

/**
 * DeviceErrorModal - Shown when beta code is already enrolled on another device
 */
const DeviceErrorModal = ({ onRetry, onClose }) => {
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl animate-in fade-in zoom-in duration-200">
        {/* Icon */}
        <div className="flex justify-center mb-4">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center">
            <Lock className="w-8 h-8 text-amber-600" />
          </div>
        </div>
        
        {/* Title */}
        <h2 className="text-xl font-bold text-slate-800 text-center mb-3">
          Code déjà utilisé sur un autre appareil
        </h2>
        
        {/* Message */}
        <div className="text-slate-600 text-center mb-6 space-y-3">
          <p>
            Ce code bêta est actuellement lié à un autre téléphone ou ordinateur.
          </p>
          <p className="text-sm text-slate-500">
            Si tu as changé d'appareil ou réinstallé l'application, contacte-nous pour transférer ton accès :
          </p>
        </div>
        
        {/* Contact Link */}
        <a 
          href="mailto:contact@thaipractice.com?subject=Transfert%20code%20bêta%20ThaiPractice"
          className="flex items-center justify-center gap-2 w-full py-3 px-4 bg-violet-100 hover:bg-violet-200 text-violet-700 rounded-xl font-medium transition-colors mb-4"
        >
          <Mail className="w-5 h-5" />
          contact@thaipractice.com
        </a>
        
        {/* Retry Button */}
        <Button
          onClick={onRetry}
          variant="outline"
          className="w-full py-3 rounded-xl"
          data-testid="device-error-retry-btn"
        >
          Réessayer
        </Button>
      </div>
    </div>
  );
};

const BetaGate = ({ onAccess }) => {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [checkingExisting, setCheckingExisting] = useState(true);
  const [showDeviceError, setShowDeviceError] = useState(false);

  // Check if there's already a valid code in localStorage
  useEffect(() => {
    const checkExistingCode = async () => {
      const storedCode = localStorage.getItem('betaCode');
      const deviceId = getOrCreateDeviceId();
      
      if (storedCode) {
        try {
          const response = await axios.post(`${API}/api/beta/validate-code`, { 
            code: storedCode,
            device_id: deviceId
          });
          
          if (response.data.valid) {
            // Store label for display
            localStorage.setItem('betaLabel', response.data.label);
            sessionStorage.setItem('beta_access', 'true');
            sessionStorage.setItem('beta_session_start', Date.now().toString());
            onAccess();
            return;
          } else if (response.data.device_error) {
            // Device mismatch - show error modal
            setShowDeviceError(true);
            localStorage.removeItem('betaCode');
            localStorage.removeItem('betaLabel');
          } else {
            // Code is no longer valid, clear it
            localStorage.removeItem('betaCode');
            localStorage.removeItem('betaLabel');
          }
        } catch (err) {
          console.error('Error checking existing code:', err);
        }
      }
      setCheckingExisting(false);
    };
    checkExistingCode();
  }, [onAccess]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!code.trim()) {
      setError('Veuillez entrer un code d\'accès');
      return;
    }
    
    setIsLoading(true);
    setError('');
    setShowDeviceError(false);
    
    const deviceId = getOrCreateDeviceId();
    
    try {
      // Use new beta code validation endpoint with device_id
      const response = await axios.post(`${API}/api/beta/validate-code`, { 
        code: code.trim(),
        device_id: deviceId
      });
      
      if (response.data.valid) {
        // Store code and label
        localStorage.setItem('betaCode', code.trim().toUpperCase());
        localStorage.setItem('betaLabel', response.data.label);
        sessionStorage.setItem('beta_access', 'true');
        sessionStorage.setItem('beta_session_start', Date.now().toString());
        onAccess();
      } else if (response.data.device_error) {
        // Device mismatch - show error modal
        setShowDeviceError(true);
      } else {
        setError(response.data.message || 'Code d\'accès invalide');
      }
    } catch (err) {
      setError('Code invalide. Contacte contact@thaipractice.com si tu penses qu\'il s\'agit d\'une erreur.');
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading while checking existing code
  if (checkingExisting) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-gradient-to-br from-violet-50 via-white to-purple-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-violet-500 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-slate-500">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Device Error Modal */}
      {showDeviceError && (
        <DeviceErrorModal 
          onRetry={() => {
            setShowDeviceError(false);
            setCode('');
          }}
          onClose={() => setShowDeviceError(false)}
        />
      )}
      
      <div className="fixed inset-0 z-50 flex items-start justify-center bg-gradient-to-br from-violet-50 via-white to-purple-50 p-4 overflow-y-auto">
        <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden my-4">
          {/* Header */}
          <div className="bg-gradient-to-r from-violet-500 to-purple-600 p-6 text-white text-center">
            <h1 className="font-heading text-3xl font-bold mb-2">ThaiPractice</h1>
            <p className="text-violet-100 text-lg">Pratique active du thaï, sans romanisation.</p>
          </div>

          <div className="p-5 space-y-5">
            {/* Description */}
            <p className="text-slate-600 text-center text-sm">
              Une application pour <strong>lire</strong>, <strong>écrire</strong>, <strong>écouter</strong>, <strong>parler</strong> et <strong>pratiquer</strong> le thaï directement avec l'écriture thaïe.
            </p>

            {/* Beta Notice */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-amber-800 mb-1">Bêta privée</h3>
                  <p className="text-sm text-amber-700 mb-2">
                    Cette version est réservée aux testeurs. L'objectif est de vérifier la fiabilité pédagogique avant une ouverture plus large.
                  </p>
                  <p className="text-xs text-amber-600">
                    <strong>À vérifier en priorité :</strong> histoires IA, vocabulaire, écriture thaïe, écoute, test de prononciation, feedback de Manee.
                  </p>
                </div>
              </div>
            </div>

            {/* What to test */}
            <div>
              <h3 className="font-semibold text-slate-800 mb-2 flex items-center gap-2 text-sm">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                Que tester ?
              </h3>
              <ul className="grid grid-cols-2 gap-1.5 text-xs text-slate-600">
                <li className="flex items-center gap-1.5">
                  <BookOpen className="w-3.5 h-3.5 text-violet-500 flex-shrink-0" />
                  Générer une histoire IA
                </li>
                <li className="flex items-center gap-1.5">
                  <BookMarked className="w-3.5 h-3.5 text-violet-500 flex-shrink-0" />
                  Pratiquer le vocabulaire
                </li>
                <li className="flex items-center gap-1.5">
                  <Pencil className="w-3.5 h-3.5 text-fuchsia-500 flex-shrink-0" />
                  Essayer l'écriture thaïe
                </li>
                <li className="flex items-center gap-1.5">
                  <Volume2 className="w-3.5 h-3.5 text-fuchsia-500 flex-shrink-0" />
                  Écouter les mots/phrases
                </li>
                <li className="flex items-center gap-1.5">
                  <Mic className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                  Test de prononciation
                </li>
                <li className="flex items-center gap-1.5">
                  <MessageCircle className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
                  Explications de Manee
                </li>
              </ul>
            </div>

            {/* What to report */}
            <div>
              <h3 className="font-semibold text-slate-800 mb-2 flex items-center gap-2 text-sm">
                <Bug className="w-4 h-4 text-red-500" />
                Que signaler ?
              </h3>
              <div className="text-xs text-slate-600 grid grid-cols-2 gap-x-3 gap-y-1">
                <span>• Histoire incohérente</span>
                <span>• Personnages qui changent</span>
                <span>• Doublons vocabulaire</span>
                <span>• Police difficile à lire</span>
                <span>• Problème audio</span>
                <span>• Prononciation peu fiable</span>
                <span>• Consigne confuse</span>
                <span>• Traduction incorrecte</span>
                <span>• Manee trop courte/longue</span>
                <span>• Présence de romanisation</span>
              </div>
            </div>

            {/* Important Rules & Pedagogical Approach */}
            <div className="bg-gradient-to-br from-violet-50 via-orange-50 to-amber-50 border border-violet-200 rounded-xl p-4 space-y-4">
              {/* Règle importante */}
              <div>
                <h4 className="text-sm font-semibold text-violet-800 mb-1.5">Règle importante</h4>
                <p className="text-xs text-slate-700">
                  ThaiPractice n'utilise pas la romanisation. L'objectif est d'apprendre directement avec l'écriture thaïe.
                </p>
              </div>

              {/* Pourquoi c'est crucial */}
              <div>
                <h4 className="text-sm font-semibold text-orange-700 mb-1.5">Pourquoi c'est crucial</h4>
                <p className="text-xs text-slate-600 leading-relaxed">
                  Au bout d'un moment, on ne voit plus des syllabes, on voit des sons. C'est le moment où l'écriture thaïe cesse d'être un obstacle et devient ton accès direct à la langue — avec toutes ses nuances tonales, ses voyelles longues et courtes, ses consonnes aspirées. La romanisation t'enferme dans des approximations qui plafonnent. L'écriture thaïe te donne les mots tels qu'ils existent vraiment dans la tête des locuteurs natifs. C'est la fondation de la fluidité.
                </p>
              </div>

              {/* Positionnement */}
              <div>
                <h4 className="text-sm font-semibold text-violet-700 mb-1.5">Positionnement</h4>
                <p className="text-xs text-slate-600 leading-relaxed mb-2">
                  ThaiPractice est un <strong>complément</strong>, pas une méthode complète. L'app suppose que tu utilises ou utiliseras d'autres ressources pédagogiques pour l'écoute, l'alphabet, l'écriture, la lecture et les explications par des locuteurs natifs.
                </p>
                <p className="text-xs text-slate-600 leading-relaxed">
                  ThaiPractice se concentre sur la <strong>pratique active</strong> : écriture, prononciation, vocabulaire, conversations avec Manee, histoires personnalisées. C'est l'étape qui consolide et applique ce que tu as appris ailleurs.
                </p>
              </div>

              {/* Manee mention */}
              <div className="bg-pink-50/50 rounded-lg p-3 border border-pink-200/50">
                <h4 className="text-sm font-semibold text-pink-700 mb-1">Manee est là pour toi</h4>
                <p className="text-xs text-slate-600 leading-relaxed">
                  Tu peux poser à Manee toutes tes questions sur la <strong>langue thaïe</strong> (grammaire, prononciation, tons, écriture, vocabulaire) et sur la <strong>culture thaïe</strong> (politesse, usages sociaux, expressions, contexte). N'hésite pas — c'est ce qu'elle aime faire.
                </p>
              </div>

              {/* Audio mention */}
              <div className="pt-2 border-t border-violet-200/50">
                <p className="text-xs text-slate-500 italic">
                  <strong>Audio :</strong> Généré par IA pour l'entraînement à l'écoute et à la prononciation.
                </p>
              </div>
            </div>

            {/* Access Form */}
            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <label htmlFor="beta-code" className="block text-sm font-medium text-slate-700 mb-1.5">
                  Code d'accès bêta
                </label>
                <Input
                  id="beta-code"
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="Entrez votre code"
                  className="text-center text-lg tracking-wider"
                  autoComplete="off"
                  disabled={isLoading}
                  data-testid="beta-code-input"
                />
                {error && (
                  <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {error}
                  </p>
                )}
              </div>
              
              <Button
                type="submit"
                disabled={isLoading}
                className="w-full bg-gradient-to-r from-violet-500 to-purple-500 hover:from-violet-600 hover:to-purple-600 text-white rounded-xl py-5 text-lg font-semibold"
                data-testid="start-beta-btn"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Vérification...
                  </>
                ) : (
                  'Commencer la bêta'
                )}
              </Button>
            </form>

            {/* Quick Access Info */}
            <div className="text-center pt-1">
              <p className="text-xs text-slate-400">
                Après connexion : Histoires • Vocabulaire • Écriture • Prononciation
              </p>
            </div>

            {/* Ressources complémentaires - Section clairement séparée */}
            <details className="mt-4 pt-3 border-t border-slate-200/50">
              <summary className="text-xs text-slate-400 cursor-pointer hover:text-slate-500 select-none">
                Ressources complémentaires recommandées (non affiliées)
              </summary>
              <div className="mt-3 p-3 bg-slate-50/50 rounded-lg">
                <p className="text-[10px] text-slate-400 italic mb-2">
                  Ces ressources tierces ne sont pas affiliées à ThaiPractice. Nous les mentionnons uniquement à titre informatif pour les apprenants qui cherchent des contenus complémentaires.
                </p>
                <ul className="text-[11px] text-slate-500 space-y-1">
                  <li>• Écoute immersive : Comprehensible Thai, Understand Thai, Riam Thai</li>
                  <li>• Alphabet et lecture : Comprehensible Thai, ToDio Thai</li>
                  <li>• Explications par locuteurs natifs : I Get Thais, KOD THAI</li>
                </ul>
              </div>
            </details>
          </div>
        </div>
      </div>
    </>
  );
};

export default BetaGate;
